import { Inject, Injectable } from '@angular/core';
import { CanPipe, ClientEnvironment, ENVIRONMENT, FilterAllOptions, StorageUtilityLib, anyAsset } from '@thehubz/client/core';
import { Asset } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata } from '@thehubz/shared/model';
import { filterEmptyObjects, filterObj, multiFilterData } from '../utils/asset-filters.util';
import { AssetCommonService } from './asset-common.service';

export interface FilterProps {
  assetFilterString: string;
  filterData?: AnyType;
  sortData?: AnyType;
  sortFlag?: boolean;
  configObject?: AnyType;
  collectionId?: string;
}

@Injectable()
export class AssetFiltersService {
  AnyAsset = anyAsset;
  brandingKey?: CustomClients;
  defaultSort: { [key: string]: number } = { createdAt: -1 };

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    private canPipe: CanPipe,
    private storageUtility: StorageUtilityLib,
    private assetCommonService: AssetCommonService,
  ) {
    this.brandingKey = this.env.brandingKey;
  }

  setFilterData(filterProp: FilterProps, filterOptions: FilterAllOptions, searchText: string) {
    if (!filterProp.filterData) {
      this.clearFilters(filterOptions);
      if (filterProp.collectionId) this.initializeCollectionIdFilter(filterOptions, filterProp.collectionId);
      this.applyStatusFilter(filterOptions);
      this.setSortFlag(filterOptions, filterProp);
      this.storeSelectedFilterData(filterOptions, filterProp, searchText);
      this.storeInSession(filterOptions, filterProp);
      return;
    }
    if (filterProp.collectionId) {
      this.initializeCollectionIdFilter(filterOptions, filterProp.collectionId);
    }
    if (filterProp.filterData) {
      this.initializeFilters(filterOptions, filterProp);
      this.applyBrandSpecificFilters(filterOptions, filterProp);
    }
    this.applyStatusFilter(filterOptions);
    this.storeSelectedFilterData(filterOptions, filterProp, searchText);
    this.storeInSession(filterOptions, filterProp);
  }

  clearFilters(filterOptions: FilterAllOptions) {
    filterOptions['filters'] = {};
    filterOptions['selectedFilterData'] = {};
  }

  initializeCollectionIdFilter(filterOptions: FilterAllOptions, collectionId: string) {
    filterOptions.filters['collectionId'] = collectionId;
  }

  initializeFilters(filterOptions: FilterAllOptions, filterProp: FilterProps) {
    const filterData = filterProp.filterData;

    delete filterOptions['filters']['brand.name'];

    filterOptions['filters']['searchText'] = filterData['searchText'] ? (filterData['searchText'].name ?? filterData['searchText']) : undefined;
    filterOptions['filters']['plannedDeletion'] = filterData['plannedForDeletion'] ? { $exists: true, $ne: null } : undefined;

    this.setStatusFilter(filterOptions, filterData);
    this.setAssetTypeFilter(filterOptions, filterData);
    this.setTagsFilter(filterOptions, filterData);
    this.setRelationTypeFilter(filterOptions, filterData);
    this.setSortFlag(filterOptions, filterProp);

    this.dateFilters(filterOptions, filterData);
    this.customFieldsFilters(filterOptions, filterData);
    this.buyoutRightsFilters(filterOptions, filterData);
    this.buyoutRightsFiltersMake(filterOptions, filterData);
  }

  setStatusFilter(filterOptions: FilterAllOptions, filterData: AnyType) {
    const statusIDs = this.extractStatusIDs(filterData);
    filterOptions['filters']['status'] = statusIDs.length > 0 ? { $in: statusIDs } : undefined;
  }

  extractStatusIDs(filterData: AnyType): string[] {
    if (filterData['status'] && (filterData['status']._id || filterData['status'].id)) {
      return [filterData['status']._id || filterData['status'].id];
    }
    return filterData['status'] && filterData['status'].length ? filterData['status']?.map((status: AnyType) => status.id) : [];
  }

  setAssetTypeFilter(filterOptions: FilterAllOptions, filterData: AnyType) {
    delete filterOptions['filters']['assetType'];
    const assetType = filterData['assetType'];

    if (Array.isArray(assetType)) {
      filterOptions['filters']['assetType'] = assetType.map((type: AnyType) => type.id);
    } else if (assetType) {
      filterOptions['filters']['assetType'] = typeof assetType === 'string' ? assetType : assetType._id || assetType.id;
    }
  }

  setTagsFilter(filterOptions: FilterAllOptions, filterData: AnyType) {
    filterOptions['filters'][AssetMetadata.General.TAGS] = filterData[AssetMetadata.General.TAGS]?.length
      ? { $all: filterData[AssetMetadata.General.TAGS].map((tag: AnyType) => tag._id) }
      : undefined;
  }

  setRelationTypeFilter(filterOptions: FilterAllOptions, filterData: AnyType) {
    filterOptions['filters']['relationType'] = filterData['relationType']?.length ? filterData['relationType'].map((type: AnyType) => type._id) : undefined;
  }

  setSortFlag(filterOptions: FilterAllOptions, filterProp: FilterProps) {
    if (filterProp.sortFlag) {
      const defaultSorting = this.defaultSort;
      filterOptions['sort'] = filterProp.sortData || defaultSorting;
    }
  }

  domainsFilters(filterOptions: FilterAllOptions, filterData?: AnyType) {
    const domainProperties = ['agency', 'client', 'brand', 'category', 'product', 'subcategory', 'brandMarke', 'brandModel'];
    domainProperties.forEach((prop) => {
      if (filterData && filterData[prop]) filterOptions['filters'][`domains.${prop}`] = filterData[prop]._id;
      else filterOptions['filters'][`domains.${prop}`] = undefined;
    });
  }

  dateFilters(filterOptions: FilterAllOptions, filterData?: AnyType) {
    const validUntil = filterData['expiryDate'] ? filterData['expiryDate'] : '';
    if (validUntil && validUntil !== '') {
      filterOptions['filters']['expiryDate'] = { $lte: validUntil };
    } else if (filterOptions['filters']['expiryDate']) {
      delete filterOptions['filters']['expiryDate'];
    }

    this.constructDateFilters(filterData, filterOptions, [
      { from: 'createdDate_from', to: 'createdDate_to', key: 'createdAt' },
      { from: 'expiryDate_from', to: 'expiryDate_to', key: 'expiryDate' },
      { from: 'plannedForDeletion_from', to: 'plannedForDeletion_to', key: 'plannedDeletion' },
      { from: 'publishedDate_from', to: 'publishedDate_until', key: 'publishStartDate' },
      { from: 'publishedDate_from', to: 'publishedDate_until', key: 'publishEndDate' },
    ]);
  }

  constructDateFilters(filterData: AnyType, filterOptions: FilterAllOptions, dateFields: { from: string; to: string; key: string }[]): void {
    dateFields.forEach(({ from, to, key }) => {
      const dateFrom = filterData[from] || '';
      const dateTo = filterData[to] || '';
      this.constructDateFilter(dateFrom, dateTo, key, filterOptions);
    });
  }

  constructDateFilter(dateFrom: AnyType, dateTo: AnyType, dateKey: string, filterOptions: FilterAllOptions) {
    if (dateFrom || dateTo) {
      if (!filterOptions['filters'][dateKey]) {
        filterOptions['filters'][dateKey] = {};
      }
      if (dateFrom) {
        filterOptions['filters'][dateKey]['$gte'] = dateFrom;
      } else {
        delete filterOptions['filters'][dateKey]['$gte'];
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setDate(toDate.getDate() + 1); // Add one day
        filterOptions['filters'][dateKey]['$lte'] = toDate;
      } else {
        delete filterOptions['filters'][dateKey]['$lte'];
      }
    } else {
      if (filterOptions.filters[dateKey] && Object.keys(filterOptions.filters[dateKey]).length === 0) {
        delete filterOptions.filters[dateKey];
      }
    }
  }

  customFieldsFilters(filterOptions: FilterAllOptions, filterData?: AnyType) {
    /* set filterObject for Arval client */
    if (this.brandingKey === CustomClients.Arval) this.multiSelectFilterArval(filterOptions, filterData);
    filterOptions['filters']['customFields.subject'] = filterData['subject'] ? filterData['subject']._id : undefined;
    filterOptions['filters']['customFields.assetCategory'] = filterData['assetCategory'] ? filterData['assetCategory'] : undefined;
    filterOptions['filters']['customFields.lufthansaContent'] = filterData['lufthansaContent'] ? filterData['lufthansaContent']._id : undefined;
    filterOptions['filters']['customFields.lufthansaProduct'] = filterData['lufthansaProduct'] ? filterData['lufthansaProduct']._id : undefined;
    filterOptions['filters']['customFields.channel'] = filterData['channel'] ? filterData['channel']._id : undefined;
    filterOptions['filters']['customFields.type'] = filterData['type'] ? filterData['type']._id : undefined;
    filterOptions['filters']['mainFile.ext'] = filterData['fileType'] ? filterData['fileType'].id : undefined;
    if (this.brandingKey === CustomClients.Standard) this.multiSelectFilter(filterOptions, filterData);
  }

  multiSelectFilterArval(filterOptions: FilterAllOptions, filter: AnyType) {
    const orSearchTextIndex = filterOptions['filters']['$or']?.findIndex((or: AnyType) => 'name' in or);
    if (filter && (filter['alignment'] || filter['ageGroup'] || filter['groupOfPeople'])) {
      if (orSearchTextIndex !== undefined && orSearchTextIndex !== -1) {
        filterOptions['filters'] = {
          ...filterOptions['filters'],
          $and: [
            {
              $or: [...filterOptions['filters']['$or']],
            },
            {
              $or: filterEmptyObjects([
                { ...multiFilterData(filter['alignment'], 'alignment') },
                { ...multiFilterData(filter['ageGroup'], 'ageGroup') },
                { ...multiFilterData(filter['groupOfPeople'], 'groupOfPeople') },
              ]),
            },
          ],
        };
      } else {
        filterOptions['filters'] = {
          ...filterOptions['filters'],
          $and: [
            {
              $or: filterEmptyObjects([
                { ...multiFilterData(filter['alignment'], 'alignment') },
                { ...multiFilterData(filter['ageGroup'], 'ageGroup') },
                { ...multiFilterData(filter['groupOfPeople'], 'groupOfPeople') },
              ]),
            },
          ],
        };
      }
    } else {
      delete filterOptions['filters']['$and'];
    }
  }

  multiSelectFilter(filterOptions: FilterAllOptions, filter: AnyType) {
    const multiFilterKeys = ['assetCategory'];
    for (const key in filter) {
      if (filter[key].length > 0 && typeof filter[key] !== 'string' && multiFilterKeys.includes(key)) {
        const filterOption: AnyType = [];
        filter[key].forEach((option: AnyType) => {
          filterOption.push(option._id || option.id || option);
        });
        if (filterOption.length > 0) {
          filterOptions['filters']['customFields.' + key] = { $in: filterOption };
        }
      } else if (typeof filter[key] != 'string' && multiFilterKeys.includes(key)) {
        const filterOption = filter[key].name;
        filterOptions['filters']['customFields.' + key] = filterOption;
      }
    }
  }
  /**
   * Update this.filterObject with buyout filters
   * @param filterData
   */
  buyoutRightsFilters(filterOptions: FilterAllOptions, filterData?: AnyType) {
    filterOptions['filters']['usageRights.creatorDepartment'] = filterObj(filterData['creatorDepartment']);
    filterOptions['filters']['usageRights.buyoutRightsTitle'] = filterObj(filterData['buyout']);
    filterOptions['filters']['usageRights.buyoutRights.licenseRegion'] = filterObj(filterData['licenseRegion']);
  }

  buyoutRightsFiltersMake(filterOptions: FilterAllOptions, filterData?: AnyType) {
    filterOptions['filters']['makeBuyoutRights.buyoutRightsTitle'] = filterData['buyoutRightsTitle'] ? filterData['buyoutRightsTitle'] : undefined;
    filterOptions['filters']['makeBuyoutRights.buyoutRights.subLevelType'] = filterData['buyoutRightsSecondLevelType']
      ? filterData['buyoutRightsSecondLevelType']
      : undefined;

    if (Array.isArray(filterData['territory']) && filterData['territory'].length > 0) {
      const allSelectedTerritory = filterData['territory'].map((territory: AnyType) => territory._id || territory.id);
      filterOptions['filters']['makeBuyoutRights.buyoutRights.territories.territory'] = { $in: allSelectedTerritory };
    } else {
      filterOptions['filters']['makeBuyoutRights.buyoutRights.territories.territory'] = undefined;
    }

    if (Array.isArray(filterData['media']) && filterData['media'].length > 0) {
      const allSelectedMedia = filterData['media'].map((media: AnyType) => media._id || media.id);
      filterOptions['filters']['makeBuyoutRights.buyoutRights.territories.media'] = { $in: allSelectedMedia };
    } else {
      filterOptions['filters']['makeBuyoutRights.buyoutRights.territories.media'] = undefined;
    }
  }

  applyBrandSpecificFilters(filterOptions: FilterAllOptions, filterProp: FilterProps) {
    const filterData = filterProp.filterData;

    if (this.brandingKey === CustomClients.Vorwerk || this.brandingKey === CustomClients.SEAT) {
      this.domainsFilters(filterOptions, filterData);
    } else {
      filterOptions['filters']['brand.name'] = filterData['brand'] ? (filterData['brand'].name ?? filterData['brand']) : undefined;
      filterOptions['filters']['client.name'] = filterData['client'] ? (filterData['client'].name ?? filterData['client']) : undefined;
    }
  }

  applyStatusFilter(filterOptions: FilterAllOptions) {
    if (!filterOptions['filters']['status']) {
      const statusList = this.assetCommonService.getAssetStatusList(this.canPipe, this.AnyAsset, filterOptions);
      filterOptions['filters']['status'] = { $in: statusList };

      if (this.brandingKey === CustomClients.Arval) {
        filterOptions['filters']['status']['$in'].push(Asset.StatusEnum.Pending);
      }
      if (this.brandingKey === CustomClients.VKB) {
        filterOptions['filters']['status']['$in'].push(Asset.StatusEnum.Expired);
      }

      if (!filterOptions['filters']['status']['$in'].length) {
        filterOptions['filters']['status'] = undefined;
      }
    }
  }

  storeSelectedFilterData(filterOptions: FilterAllOptions, filterProp: FilterProps, searchText: string) {
    // Clean filtered data and store it in a separate query string
    const selectedFilterData: AnyType = filterProp.filterData ? Object.fromEntries(Object.entries(filterProp.filterData).filter(([_, v]) => v && v != '')) : {};

    this.applyClientSpecificFilters(filterOptions, selectedFilterData, filterProp.filterData);

    for (const key in selectedFilterData) {
      if (selectedFilterData[key]._id || selectedFilterData[key].id) {
        selectedFilterData[key] = {
          _id: selectedFilterData[key]._id || selectedFilterData[key].id,
          name: selectedFilterData[key].name,
        };
      }
    }
    filterOptions['selectedFilterData'] = selectedFilterData;
    filterOptions['searchedText'] = searchText;
  }

  applyClientSpecificFilters(filterOptions: FilterAllOptions, selectedFilterData: AnyType, filterData: AnyType) {
    switch (this.brandingKey) {
      // set filterObject for Vorwerk client
      case CustomClients.Vorwerk:
        this.multiSelectFilterVorwerk(filterOptions, selectedFilterData);
        break;
      // set filterObject for Lufthansa client
      case CustomClients.LufthansaMediacenter:
      case CustomClients.LufthansaMedialounge:
        this.multiSelectFilterLufthansa(filterOptions, filterData);
        break;
      // set filterObject for VKB client
      case CustomClients.VKB:
        this.multiSelectFilterVKB(filterOptions, filterData);
        break;
      // set filterObject for SEAT client
      case CustomClients.SEAT:
        this.multiSelectFilterSeat(filterOptions, filterData);
        break;
    }
  }

  multiSelectFilterLufthansa(filterOptions: FilterAllOptions, filter: AnyType) {
    const skipKeys = ['status', AssetMetadata.General.TAGS];
    for (const key in filter) {
      if (filter[key].length > 0 && typeof filter[key] != 'string' && !this.shouldSkipFilter(key, skipKeys)) {
        const filterOption: AnyType = [];
        filter[key].forEach((option: AnyType) => {
          if (key === 'brand') {
            filterOption.push(option.name);
            filterOptions['filters'][key + '.name'] = { $in: filterOption };
          } else {
            filterOption.push(option._id || option.id);
            filterOptions['filters'][key] = { $in: filterOption };
          }
        });
      }
    }
  }

  multiSelectFilterVKB(filterOptions: FilterAllOptions, filter: AnyType) {
    const skipKeys = ['status', AssetMetadata.General.TAGS, 'createdDate_to', 'createdDate_from', 'expiryDate_from', 'expiryDate_to', 'searchText'];
    for (const key in filter) {
      if (filter[key].length > 0 && typeof filter[key] !== 'string' && !this.shouldSkipFilter(key, skipKeys)) {
        const filterOption: AnyType = [];
        filter[key].forEach((option: AnyType) => {
          filterOption.push(option._id || option.id);
        });
        if (filterOption.length > 0) {
          filterOptions['filters']['customFields.' + key] = { $in: filterOption };
        }
      } else if (typeof filter[key] != 'string' && !this.shouldSkipFilter(key, skipKeys)) {
        const filterOption = filter[key].name;
        filterOptions['filters']['customFields.' + key] = filterOption;
      }
    }
  }

  multiSelectFilterVorwerk(filterOptions: FilterAllOptions, filter: AnyType) {
    const skipKeys = ['status', AssetMetadata.General.TAGS, 'relationType', 'creatorDepartment', 'buyout', 'licenseRegion', 'relationType'];
    for (const key in filter) {
      if (filter[key].length > 0 && typeof filter[key] != 'string' && !this.shouldSkipFilter(key, skipKeys)) {
        const filterOption: AnyType = [];
        filter[key].forEach((option: AnyType) => {
          filterOption.push(option._id || option.id);
          if (key === 'assetType') {
            filterOptions['filters']['assetType'] = { $in: filterOption };
          } else {
            filterOptions['filters']['customFields.' + key] = { $in: filterOption };
          }
        });
      }
    }
  }

  multiSelectFilterSeat(filterOptions: FilterAllOptions, filter: AnyType) {
    const skipKeys = ['status', AssetMetadata.General.TAGS, 'client', 'brand', 'assetType', 'expiryDate_from', 'expiryDate_to'];
    for (const key in filter) {
      if (filter[key].length > 0 && typeof filter[key] != 'string' && !this.shouldSkipFilter(key, skipKeys)) {
        const filterOption: AnyType = [];
        filter[key].forEach((option: AnyType) => {
          filterOption.push(option._id || option.id);
          filterOptions['filters']['customFields.' + key] = { $in: filterOption };
        });
      }
    }
  }

  private shouldSkipFilter(key: string, skipKeys: string[]): boolean {
    return skipKeys.includes(key) || key.startsWith('searchText[');
  }

  storeInSession(filterOptions: FilterAllOptions, filterProp: FilterProps) {
    const tempFilterObject = { ...filterOptions, selectedFilterData: filterOptions['selectedFilterData'] };
    tempFilterObject['skip'] = 0;
    if (!filterProp.configObject || filterProp.configObject?.callFrom !== 'dashboard') {
      if (filterProp.collectionId) {
        const collectionFilters = this.storageUtility.getSessionStorageNullable(filterProp.assetFilterString) || undefined;
        this.storageUtility.setSessionStorage(filterProp.assetFilterString, { ...collectionFilters, [filterProp.collectionId]: tempFilterObject });
      } else {
        this.storageUtility.setSessionStorage(filterProp.assetFilterString, tempFilterObject);
      }
    }
  }

  updateSortingSessionStorage(sortData: AnyType, filterString: string) {
    const filterDataObject: FilterAllOptions = this.storageUtility.getSessionStorage(filterString);
    filterDataObject['sort'] = sortData;
    this.storageUtility.setSessionStorage(filterString, filterDataObject);
  }

  getSearchTextFilters(selectedFilters?: Record<string, AnyType>) {
    const searchTextFilters: Record<string, AnyType> = {};
    if (selectedFilters) {
      Object.keys(selectedFilters).forEach((key) => {
        if (key.startsWith('searchText')) {
          searchTextFilters[key] = selectedFilters[key];
        }
      });
    }
    return searchTextFilters;
  }
}
